import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";
import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import { useAuth } from "~/context/AuthContext";
import {
  computePreviousPeriod,
  useAIVisibilityFilters,
  type ComparisonMode,
} from "~/context/AIVisibilityFiltersContext";
import { useSubscription } from "~/context/SubscriptionContext";
import {
  getPresetRange,
  PRESET_GROUPS,
  type PresetDateRange,
  type PresetId,
} from "~/lib/date-range-presets";
import { cn } from "~/lib/utils";

type EditingTarget = "primary" | "comparison";
type PickerView = "presets" | "calendar";

const COMPARISON_PRESETS: { label: string; value: ComparisonMode }[] = [
  { label: "Previous period", value: "previous_period" },
  { label: "Custom range", value: "custom" },
];

function normalizeRange(range: DateRange): DateRange {
  const from = range.from ? new Date(range.from) : undefined;
  const to = range.to ? new Date(range.to) : undefined;
  if (from) from.setHours(0, 0, 0, 0);
  if (to) to.setHours(23, 59, 59, 999);
  return { from, to };
}

function formatShort(date: Date, withYear: boolean): string {
  const day = date.toLocaleDateString("en-GB", { day: "2-digit" });
  const month = date.toLocaleDateString("en-GB", { month: "short" });
  return withYear ? `${day} ${month}, ${date.getFullYear()}` : `${day} ${month}`;
}

function formatRangeLabel(range: PresetDateRange): string {
  const sameYear = range.from.getFullYear() === range.to.getFullYear();
  return `${formatShort(range.from, !sameYear)} - ${formatShort(range.to, true)}`;
}

function formatTriggerDate(date?: Date | null, withYear = false): string {
  if (!date) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    ...(withYear ? { year: "numeric" } : {}),
  });
}

function formatTriggerRange(from?: Date | null, to?: Date | null): string {
  const sameYear =
    from && to ? from.getFullYear() === to.getFullYear() : true;
  return `${formatTriggerDate(from, !sameYear)} - ${formatTriggerDate(to, true)}`;
}

function formatFieldDate(date?: Date): string {
  if (!date) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

interface AIVisibilityDatePickerProps {
  showCompareToggle?: boolean;
  hideOnTrialMode?: boolean;
  showTrialModeCharts?: boolean;
}

export default function AIVisibilityDatePicker({
  showCompareToggle = false,
  hideOnTrialMode = false,
  showTrialModeCharts = false,
}: AIVisibilityDatePickerProps) {
  const { subscription } = useSubscription();
  const { user } = useAuth();
  const {
    selectedRange,
    customRange,
    fromDate,
    toDate,
    latestDataDate,
    compareEnabled,
    setCompareEnabled,
    comparisonMode,
    setComparisonMode,
    comparisonRange,
    comparisonFromDate,
    comparisonToDate,
    applyAnalyticsDateSelection,
  } = useAIVisibilityFilters();

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<PickerView>("presets");

  const [tempPrimaryRange, setTempPrimaryRange] = useState<
    DateRange | undefined
  >(undefined);
  const [tempCompareEnabled, setTempCompareEnabled] = useState(compareEnabled);
  const [tempComparisonMode, setTempComparisonMode] =
    useState<ComparisonMode>(comparisonMode);
  const [tempComparisonRange, setTempComparisonRange] = useState<
    DateRange | undefined
  >(undefined);
  const [editingTarget, setEditingTarget] = useState<EditingTarget>("primary");
  const [calendarMonth, setCalendarMonth] = useState<Date | undefined>(
    undefined
  );

  const allTimeStart = useMemo(() => {
    const activeTeam = user?.teams?.find(
      (team) => String(team.id) === String(user?.team_id)
    );
    return activeTeam?.created_at ? new Date(activeTeam.created_at) : undefined;
  }, [user]);

  const presetRanges = useMemo(() => {
    const now = new Date();
    const map = new Map<PresetId, PresetDateRange>();
    PRESET_GROUPS.flat().forEach((preset) => {
      map.set(
        preset.id,
        getPresetRange(preset.id, now, allTimeStart, latestDataDate)
      );
    });
    return map;
  }, [allTimeStart, latestDataDate]);

  const isTrialing = subscription?.status === "trialing";
  const shouldHide = hideOnTrialMode && (isTrialing || showTrialModeCharts);

  useEffect(() => {
    if (open) {
      setView("presets");
    }
  }, [open]);

  const seedCalendarView = () => {
    const primary: DateRange =
      selectedRange === "custom" && customRange?.from && customRange?.to
        ? { from: customRange.from, to: customRange.to }
        : { from: fromDate, to: toDate };

    setTempPrimaryRange(primary);
    setTempCompareEnabled(compareEnabled);
    setTempComparisonMode(comparisonMode);

    if (
      comparisonMode === "custom" &&
      comparisonRange?.from &&
      comparisonRange?.to
    ) {
      setTempComparisonRange({
        from: comparisonRange.from,
        to: comparisonRange.to,
      });
    } else if (primary.from && primary.to) {
      setTempComparisonRange(computePreviousPeriod(primary.from, primary.to));
    } else {
      setTempComparisonRange(undefined);
    }

    setEditingTarget("primary");
    setCalendarMonth(primary.from ?? new Date());
    setView("calendar");
  };

  const applyPreset = (id: PresetId) => {
    const range = getPresetRange(id, new Date(), allTimeStart, latestDataDate);
    applyAnalyticsDateSelection({
      primaryRange: range,
      primaryPreset: id,
      compareEnabled,
      comparisonMode: "previous_period",
      comparisonRange: undefined,
    });
    setOpen(false);
  };

  const handleQuickCompareToggle = (checked: boolean) => {
    setCompareEnabled(checked);
    if (checked) {
      setComparisonMode("previous_period");
    }
  };

  const comparisonDisplayRange = useMemo<DateRange | undefined>(() => {
    if (tempComparisonMode === "custom") {
      return tempComparisonRange;
    }
    if (tempPrimaryRange?.from && tempPrimaryRange?.to) {
      return computePreviousPeriod(tempPrimaryRange.from, tempPrimaryRange.to);
    }
    return undefined;
  }, [tempComparisonMode, tempComparisonRange, tempPrimaryRange]);

  const calendarModifiers = useMemo(() => {
    if (editingTarget === "primary") {
      if (!tempCompareEnabled || !comparisonDisplayRange?.from) return {};
      return {
        compareRange: comparisonDisplayRange,
        compareStart: comparisonDisplayRange.from,
        compareEnd: comparisonDisplayRange.to,
      };
    }
    if (!tempPrimaryRange?.from) return {};
    return {
      primaryRange: tempPrimaryRange,
      primaryStart: tempPrimaryRange.from,
      primaryEnd: tempPrimaryRange.to,
    };
  }, [editingTarget, tempCompareEnabled, comparisonDisplayRange, tempPrimaryRange]);

  const handleCalendarSelect = (range: DateRange | undefined) => {
    if (editingTarget === "primary") {
      setTempPrimaryRange(range);
    } else {
      setTempComparisonRange(range);
      setTempComparisonMode("custom");
    }
  };

  const handleComparisonPreset = (value: ComparisonMode) => {
    setTempComparisonMode(value);
    if (value === "custom") {
      setEditingTarget("comparison");
      if (
        (!tempComparisonRange?.from || !tempComparisonRange?.to) &&
        tempPrimaryRange?.from &&
        tempPrimaryRange?.to
      ) {
        const seeded = computePreviousPeriod(
          tempPrimaryRange.from,
          tempPrimaryRange.to
        );
        setTempComparisonRange(seeded);
        setCalendarMonth(seeded.from);
      }
    } else {
      setEditingTarget("primary");
    }
  };

  const focusComparisonEditing = () => {
    if (!tempCompareEnabled) return;
    setEditingTarget("comparison");
    if (tempComparisonMode !== "custom") {
      setTempComparisonMode("custom");
      if (comparisonDisplayRange?.from && comparisonDisplayRange?.to) {
        setTempComparisonRange(comparisonDisplayRange);
        setCalendarMonth(comparisonDisplayRange.from);
      }
    } else if (tempComparisonRange?.from) {
      setCalendarMonth(tempComparisonRange.from);
    }
  };

  const isPrimaryComplete = Boolean(
    tempPrimaryRange?.from && tempPrimaryRange?.to
  );
  const isComparisonComplete =
    !tempCompareEnabled ||
    tempComparisonMode === "previous_period" ||
    Boolean(tempComparisonRange?.from && tempComparisonRange?.to);
  const canApply = isPrimaryComplete && isComparisonComplete;

  const handleApplyCalendar = () => {
    if (!canApply || !tempPrimaryRange) return;

    const comparison =
      tempCompareEnabled &&
      tempComparisonMode === "custom" &&
      tempComparisonRange
        ? normalizeRange(tempComparisonRange)
        : undefined;

    applyAnalyticsDateSelection({
      primaryRange: normalizeRange(tempPrimaryRange),
      primaryPreset: "custom",
      compareEnabled: tempCompareEnabled,
      comparisonMode: tempComparisonMode,
      comparisonRange: comparison,
    });
    setOpen(false);
  };

  const triggerLabel = useMemo(() => {
    const main = formatTriggerRange(fromDate, toDate);
    if (compareEnabled && comparisonFromDate && comparisonToDate) {
      return `${main} vs ${formatTriggerRange(comparisonFromDate, comparisonToDate)}`;
    }
    return main;
  }, [fromDate, toDate, compareEnabled, comparisonFromDate, comparisonToDate]);

  if (shouldHide) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline">
          <CalendarIcon className="w-4 h-4 mr-2" />
          <span className="text-sm">{triggerLabel}</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-auto overflow-hidden p-0" align="end">
        {view === "presets" ? (
          <div
            key="presets"
            className="w-80 animate-in fade-in slide-in-from-left-4 duration-300 ease-out"
          >
            <div className="px-2 py-2">
              <div className="px-2 pb-1 pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Date range
              </div>

              {PRESET_GROUPS.map((group, groupIndex) => (
                <div
                  key={groupIndex}
                  className={cn(
                    "py-1",
                    groupIndex > 0 && "border-t border-border/60"
                  )}
                >
                  {group.map((preset) => {
                    const range = presetRanges.get(preset.id);
                    const isActive = selectedRange === preset.id;
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => applyPreset(preset.id)}
                        className={cn(
                          "flex w-full items-center justify-between gap-4 rounded-md px-2 py-1.5 text-left transition-colors",
                          isActive ? "bg-muted" : "hover:bg-muted/60"
                        )}
                      >
                        <span
                          className={cn(
                            "text-sm",
                            isActive
                              ? "font-medium text-foreground"
                              : "text-foreground"
                          )}
                        >
                          {preset.label}
                        </span>
                        {range && (
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {formatRangeLabel(range)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            {showCompareToggle && (
              <button
                type="button"
                onClick={() => handleQuickCompareToggle(!compareEnabled)}
                className={cn(
                  "flex w-full items-center justify-between gap-4 border-t px-4 py-3 text-left transition-colors hover:bg-muted/60",
                  compareEnabled && "bg-muted"
                )}
              >
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium">Compare</span>
                  <span className="text-xs text-muted-foreground">
                    vs previous period
                  </span>
                </div>
                <Switch
                  checked={compareEnabled}
                  className="pointer-events-none"
                />
              </button>
            )}

            <button
              type="button"
              onClick={seedCalendarView}
              className={cn(
                "flex w-full items-center justify-between gap-4 border-t px-4 py-3 text-left transition-colors hover:bg-muted/60",
                selectedRange === "custom" && "bg-muted"
              )}
            >
              <span className="text-sm font-medium">Custom date range</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        ) : (
          <div
            key="calendar"
            className="flex flex-col animate-in fade-in slide-in-from-right-4 duration-300 ease-out"
          >
            <button
              type="button"
              onClick={() => setView("presets")}
              className="flex items-center gap-1.5 border-b px-4 py-3 text-left text-sm font-medium transition-colors hover:bg-muted/60"
            >
              <ChevronLeft className="h-4 w-4 text-muted-foreground" />
              Custom date range
            </button>

            <div className="flex flex-col md:flex-row">
              <div className="p-4">
                <Calendar
                  mode="range"
                  showOutsideDays={false}
                  selected={
                    editingTarget === "primary"
                      ? tempPrimaryRange
                      : tempComparisonRange
                  }
                  onSelect={handleCalendarSelect}
                  numberOfMonths={2}
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  disabled={{ after: latestDataDate }}
                  modifiers={calendarModifiers}
                  modifiersClassNames={{
                    compareRange: "bg-teal-100 text-teal-900",
                    compareStart: "bg-teal-200 rounded-l-md",
                    compareEnd: "bg-teal-200 rounded-r-md",
                    primaryRange: "bg-orange-100 text-orange-900",
                    primaryStart: "bg-orange-200 rounded-l-md",
                    primaryEnd: "bg-orange-200 rounded-r-md",
                  }}
                />
              </div>

              <div className="flex w-full flex-col gap-4 border-t p-4 md:w-72 md:border-l md:border-t-0">
                <RangeFields
                  title="Date range"
                  accent="primary"
                  fromDate={tempPrimaryRange?.from}
                  toDate={tempPrimaryRange?.to}
                  active={editingTarget === "primary"}
                  onFocus={() => setEditingTarget("primary")}
                />

                {showCompareToggle && (
                  <>
                    <div className="flex items-center gap-2 border-t pt-4">
                      <Switch
                        checked={tempCompareEnabled}
                        onCheckedChange={setTempCompareEnabled}
                      />
                      <span className="text-sm font-medium">Compare with</span>
                    </div>

                    {tempCompareEnabled && (
                      <div className="flex flex-col gap-2">
                        <Select
                          items={COMPARISON_PRESETS}
                          value={tempComparisonMode}
                          onValueChange={(value) => {
                            if (value != null) {
                              handleComparisonPreset(value as ComparisonMode);
                            }
                          }}
                        >
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              {COMPARISON_PRESETS.map((preset) => (
                                <SelectItem
                                  key={preset.value}
                                  value={preset.value}
                                >
                                  {preset.label}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>

                        <RangeFields
                          accent="comparison"
                          fromDate={comparisonDisplayRange?.from}
                          toDate={comparisonDisplayRange?.to}
                          active={editingTarget === "comparison"}
                          onFocus={focusComparisonEditing}
                        />
                      </div>
                    )}
                  </>
                )}

                <div className="mt-auto flex justify-end gap-2 border-t pt-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-600"
                    onClick={() => setOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleApplyCalendar}
                    disabled={!canApply}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface RangeFieldsProps {
  title?: string;
  accent: "primary" | "comparison";
  fromDate?: Date;
  toDate?: Date;
  active: boolean;
  onFocus: () => void;
}

function RangeFields({
  title,
  accent,
  fromDate,
  toDate,
  active,
  onFocus,
}: RangeFieldsProps) {
  const dotClass = accent === "primary" ? "bg-orange-400" : "bg-teal-400";

  return (
    <div className="flex flex-col gap-2">
      {title && (
        <div className="flex items-center gap-2">
          <span className={cn("h-2 w-2 rounded-full", dotClass)} />
          <span className="text-xs font-medium text-muted-foreground">
            {title}
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onFocus}
          className={cn(
            "flex flex-col items-start rounded-md border px-2 py-1 text-left transition-colors",
            active
              ? "border-primary ring-1 ring-primary/40"
              : "border-input hover:bg-muted/50"
          )}
        >
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            From
          </span>
          <span className="text-sm">{formatFieldDate(fromDate)}</span>
        </button>
        <button
          type="button"
          onClick={onFocus}
          className={cn(
            "flex flex-col items-start rounded-md border px-2 py-1 text-left transition-colors",
            active
              ? "border-primary ring-1 ring-primary/40"
              : "border-input hover:bg-muted/50"
          )}
        >
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            To
          </span>
          <span className="text-sm">{formatFieldDate(toDate)}</span>
        </button>
      </div>
    </div>
  );
}
